import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/results-tracker';

/**
 * Results Tracker Service
 * Handles all API calls for bet tracking and unit settings
 */

/**
 * Get authorization headers
 */
const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Handle API errors consistently
 */
const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;
        
        // Authentication errors
        if (status === 401) {
            const errorCode = data.error?.code || 'AUTH_FAILED';
            return {
                success: false,
                status,
                errorCode,
                message: data.message || data.error?.message || 'Authentication failed',
                isAuthError: true
            };
        }
        
        // Validation errors
        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid data',
                details: data.details || []
            };
        }
        
        // Not found errors
        if (status === 404) {
            return {
                success: false,
                status,
                errorCode: data.error || 'NOT_FOUND',
                message: data.message || 'Resource not found'
            };
        }
        
        // Server errors
        return {
            success: false,
            status,
            errorCode: 'SERVER_ERROR',
            message: data.error?.message || data.message || 'Server error. Please try again later.'
        };
    }
    
    // Network errors
    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const resultsTrackerService = {
    /**
     * Get all bets and unit settings for the authenticated user
     * @returns {Promise} API response with bets and unitSettings
     */
    getAllData: async () => {
        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}`, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Add a single bet
     * @param {object} bet - Bet data
     * @returns {Promise} API response with created bet
     */
    addBet: async (bet) => {
        try {
            const response = await axios.post(`${API_URL}${BASE_PATH}/bets`, bet, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Bulk import bets
     * @param {array} bets - Array of bet objects
     * @param {boolean} replaceExisting - Whether to replace existing bets
     * @returns {Promise} API response with imported bets
     */
    bulkImportBets: async (bets, replaceExisting = true) => {
        try {
            const response = await axios.post(`${API_URL}${BASE_PATH}/bets/bulk`, {
                bets,
                replaceExisting
            }, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Update an existing bet
     * @param {number} id - Bet ID
     * @param {object} bet - Updated bet data
     * @returns {Promise} API response with updated bet
     */
    updateBet: async (id, bet) => {
        try {
            const response = await axios.put(`${API_URL}${BASE_PATH}/bets/${id}`, bet, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Delete a bet
     * @param {number} id - Bet ID
     * @returns {Promise} API response
     */
    deleteBet: async (id) => {
        try {
            const response = await axios.delete(`${API_URL}${BASE_PATH}/bets/${id}`, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                message: response.data.message || 'Bet deleted successfully'
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Update unit settings
     * @param {object} settings - Unit settings data
     * @returns {Promise} API response with updated settings
     */
    updateSettings: async (settings) => {
        try {
            const response = await axios.put(`${API_URL}${BASE_PATH}/settings`, settings, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Get user's unit/stake settings
     * @returns {Promise} API response with settings data
     */
    getSettings: async () => {
        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}/settings`, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Get the user's monthly performance summary (backend-computed).
     * Defaults to the current month in the user's timezone, in units.
     * @param {object} [options]
     * @param {string} [options.month] - Target month YYYY-MM (defaults to current month)
     * @param {string} [options.display] - 'units' | 'dollars' (default 'units')
     * @param {string} [options.sport] - Optional sport filter (e.g. 'afl')
     * @returns {Promise} API response with the monthly summary
     */
    getMonthlyPerformance: async ({ month, display = 'units', sport } = {}) => {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await axios.get(`${API_URL}${BASE_PATH}/performance/monthly`, {
                headers: getAuthHeaders(),
                params: {
                    display,
                    ...(timezone ? { timezone } : {}),
                    ...(month ? { month } : {}),
                    ...(sport ? { sport } : {})
                }
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Reorder bets within a specific date
     * @param {string} date - Date in YYYY-MM-DD format
     * @param {array} betOrders - Array of { id, sort_order } objects
     * @returns {Promise} API response with reorder result
     */
    reorderBets: async (date, betOrders) => {
        try {
            const response = await axios.patch(`${API_URL}${BASE_PATH}/bets/reorder`, {
                date,
                betOrders
            }, {
                headers: getAuthHeaders()
            });
            
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default resultsTrackerService;
